// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import session from '../session';
import {steps as nodeSteps} from '../server/node';
import {
  automaticTaxStep,
  lineItemsStep,
  paymentMethodsStep,
} from '../html/steps';
import {DefaultCards, BuildInstructions} from '../../../components/left-col';

import type {StepsConfig} from '../../../data/GuideTypes';

function code(str) {
  return <code>{str}</code>;
}

const buildInstructions = [
  {
    label: 'npm',
    body: {
      message: defineMessage({
        id: 'docsApp.ib.aap.snippets.next.npm.body',
        defaultMessage: 'Install the libraries:',
      }),
    },
    command: 'npm install --save stripe @stripe/stripe-js next',
    shell: true,
  },
];

const setup = {
  heading: {
    message: defineMessage({
      id: 'docsApp.ib.aap.snippets.next.github.install.heading',
      defaultMessage: 'Install the Stripe libraries',
    }),
  },
  body: {
    message: defineMessage({
      id: 'docsApp.ib.aap.snippets.next.github.install.body',
      defaultMessage:
        'Install the packages and import them in your code. Alternatively, if you’re starting from scratch and need a <code>package.json</code> file, download the project files using the link in the code editor.',
    }),
    values: {code},
  },
  Component() {
    return <BuildInstructions instructions={buildInstructions} />;
  },
};

export const serverSteps = {
  ...nodeSteps,
  setup,
};

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.create.heading',
        defaultMessage: 'Create a Checkout Session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.create.body',
        defaultMessage: `Add an endpoint that creates a <a>Checkout Session</a> by adding a file to the <code>pages/api/</code> folder. A Checkout Session controls what your customer sees in the Stripe-hosted payment page, such as line items, the order amount and currency, and acceptable payment methods.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/checkout/sessions" target="_blank">
            {str}
          </a>
        ),
        code,
      },
    },
    response: session.react,
  },
  lineItemsStep,
  paymentMethodsStep,
  {
    file: 'server',
    id: 'mode',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.mode.heading',
        defaultMessage: 'Choose the mode',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.mode.body',
        defaultMessage: `Checkout has three modes: <code>payment</code>, <code>subscription</code>, or <code>setup</code>. Use <code>payment</code> mode for one-time purchases. Learn more about <subscriptionA>subscription</subscriptionA> and <setupA>setup</setupA> modes in the docs.`,
      }),
      values: {
        subscriptionA: (str) => (
          <a
            href="/docs/payments/checkout/set-up-a-subscription"
            target="_blank"
          >
            {str}
          </a>
        ),
        setupA: (str) => (
          <a href="/docs/payments/save-and-reuse#checkout" target="_blank">
            {str}
          </a>
        ),
        code,
      },
    },
  },
  {
    file: 'server',
    id: 'urls',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.urls.heading',
        defaultMessage: 'Supply the redirect URLs',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.urls.body',
        defaultMessage: `Specify the URLs Stripe should redirect to when your customer completes or cancels the Checkout Session. You can handle both the success and canceled states with the same URL.`,
      }),
    },
  },
  {
    id: 'preview-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.preview-page.heading',
        defaultMessage: 'Add an order preview page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.preview-page.body',
        defaultMessage:
          "Add a file under <code>pages/</code> to create a page showing a preview of the customer's order. Allow the customer to review or modify their order—once a customer is sent to the Checkout page, the order is final and cannot be modified without creating a new Checkout Session.",
      }),
      values: {code},
    },
  },
  {
    id: 'load-stripe',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.load-stripe.heading',
        defaultMessage: 'Load Stripe.js',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.load-stripe.body',
        defaultMessage: `Stripe Checkout relies on Stripe.js, Stripe’s foundational JavaScript library for collecting sensitive payment information with advanced fraud detection. Call <code>loadStripe</code> with your publishable API key. It returns a Promise that resolves with the Stripe object as soon as Stripe.js loads.`,
      }),
      values: {code},
    },
  },
  {
    id: 'add-button',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.add-button.heading',
        defaultMessage: 'Add a checkout button',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.add-button.body',
        defaultMessage:
          'Add a button to your order preview page. When your customer clicks this button, they are redirected to the Stripe-hosted payment form.',
      }),
    },
  },
  {
    id: 'fetch-checkout-session',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.fetch-checkout-session.heading',
        defaultMessage: 'Fetch a Checkout Session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.fetch-checkout-session.body',
        defaultMessage:
          'Make a request to the endpoint on your server to redirect the customer to a new Checkout Session when they click on the <strong>Checkout</strong> button.',
      }),
      values: {
        strong: (str) => <strong>{str}</strong>,
      },
    },
  },
  {
    id: 'back',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.back.heading',
        defaultMessage: 'Handle redirect back from Checkout',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.back.body',
        defaultMessage:
          'Show a message to your customer when they are redirected back to your page. Wait to fulfill orders (e.g., shipping or sending email receipts) until the payment succeeds. Since some payments may take minutes or even days to process, set up a webhook to listen for a <code>payment_intent.succeeded</code> event to reliably know when the payment is successful. Learn more about <a>fulfilling orders with Checkout</a>.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://stripe.com/docs/payments/checkout/fulfill-orders"
            target="_blank"
          >
            {str}
          </a>
        ),
        code,
      },
    },
  },
  {
    id: 'set-env-vars',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.set-env-vars.heading',
        defaultMessage: 'Set your environment variables',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.set-env-vars.body',
        defaultMessage:
          'Add your publishable and secret keys to a <code>.env</code> file. Next.js automatically loads them into your application as <a1>environment variables</a1>. If you want to listen to webhooks, also include a webhook secret, which can be created in the <a2>Dashboard</a2> or with the <a3>Stripe CLI</a3>.',
      }),
      values: {
        a1: (str) => (
          <a href="https://nextjs.org/docs/basic-features/environment-variables">
            {str}
          </a>
        ),
        a2: (str) => <a href="https://dashboard.stripe.com/webhooks">{str}</a>,
        a3: (str) => (
          <a href="https://stripe.com/docs/stripe-cli/webhooks">{str}</a>
        ),
        code,
      },
    },
  },
  {
    id: 'run-server',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.run-server.body',
        defaultMessage:
          'Start your app with <code>npm run dev</code> and navigate to <a>http://localhost:3000</a>.',
      }),
      values: {
        a: (str) => <a href="http://localhost:3000">{str}</a>,
        code,
      },
    },
  },
  {
    id: 'testing',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.testing.heading',
        defaultMessage: 'Try it out',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.testing.body',
        defaultMessage:
          'Click the checkout button to be redirected to the Checkout page. Use any of these test cards to simulate a payment.',
      }),
    },
    Component: DefaultCards,
  },
  {
    extension: 'extra',
    id: 'customize',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.customize.heading',
        defaultMessage: 'Customize the checkout page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.next.js.customize.body',
        defaultMessage:
          "You can customize the appearance of the hosted Checkout page by adding your logo and color theme in the Dashboard's <a>branding settings</a>, or by using the API to activate additional features like address collection or prefilling customer details.",
      }),
      values: {
        a: (str) => (
          <a
            href="https://dashboard.stripe.com/settings/branding"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    steps: [
      {
        id: 'prefill-email',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.prefill-email.heading',
            defaultMessage: 'Prefill customer data',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.prefill-email.body',
            defaultMessage:
              "Use <code>customer_email</code> to prefill the customer's email address in the email input field. You can also pass a <a>Customer</a> ID to <code>customer</code> field to prefill the email address field with the email stored on the Customer.",
          }),
          values: {
            a: (str) => (
              <a href="/docs/api/customers" target="_blank">
                {str}
              </a>
            ),
            code,
          },
        },
      },
      {
        id: 'submit-button',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.submit-button.heading',
            defaultMessage: 'Pick a submit button',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.submit-button.body',
            defaultMessage:
              'Configure the copy displayed on the Checkout submit button by setting the <code>submit_type</code>. There are four different submit types.',
          }),
          values: {
            code,
          },
        },
      },
      {
        id: 'address',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.address.heading',
            defaultMessage: 'Collect billing and shipping details',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.address.body',
            defaultMessage:
              "Use <code>billing_address_collection</code> and <code>shipping_address_collection</code> to collect your customer's address. <code>shipping_address_collection</code> requires a list of <code>allowed_countries</code>. Checkout displays the list of allowed countries in a dropdown on the page.",
          }),
          values: {
            code,
          },
        },
      },
      {
        id: 'testing-customized',
        section: 'testing',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.testing-customized.heading',
            defaultMessage: 'Preview the customized page',
          }),
        },
        testing: true,
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.next.js.testing-customized.body',
            defaultMessage:
              'Click the checkout button in the preview on the right to see a sample Stripe Checkout page with these additional fields. <a>Read our docs</a> for all the ways you can customize Checkout—we continuously add new features.',
          }),
          values: {
            a: (str) => (
              <a href="/docs/payments/checkout/customization" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
    ],
  },
  automaticTaxStep,
]: StepsConfig);
